const PKCE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
// Largest byte value that maps onto the charset with uniform probability -- values above this
// are discarded and redrawn, since 256 is not a multiple of PKCE_CHARSET.length and using
// `% PKCE_CHARSET.length` on the full 0-255 range would bias low-index characters (CWE-338).
const MAX_UNBIASED_BYTE = Math.floor(256 / PKCE_CHARSET.length) * PKCE_CHARSET.length - 1
export const PKCE_CODE_VERIFIER_STORAGE_KEY = 'auth.pkce.code_verifier'

const encodeBase64Url = (bytes: Uint8Array): string => {
  let binary = ''

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const createCodeVerifier = (length = 96): string => {
  const characters: string[] = []

  while (characters.length < length) {
    const randomValues = window.crypto.getRandomValues(new Uint8Array(length - characters.length))

    for (const value of randomValues) {
      if (value <= MAX_UNBIASED_BYTE) {
        characters.push(PKCE_CHARSET[value % PKCE_CHARSET.length])
      }
    }
  }

  return characters.join('')
}

const sha256Fallback = async (data: Uint8Array): Promise<ArrayBuffer> => {
  const K: number[] = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]

  const rotr = (n: number, x: number): number => (x >>> n) | (x << (32 - n))

  const pad = (msg: Uint8Array): Uint8Array => {
    const bitLen = msg.length * 8
    const padLen = (64 - ((msg.length + 9) % 64)) % 64
    const padded = new Uint8Array(msg.length + 1 + padLen + 8)
    padded.set(msg)
    padded[msg.length] = 0x80
    const view = new DataView(padded.buffer)
    view.setUint32(padded.length - 4, bitLen, false)
    return padded
  }

  const padded = pad(data)
  let [h0, h1, h2, h3, h4, h5, h6, h7] = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]

  for (let off = 0; off < padded.length; off += 64) {
    const w = new Array<number>(64)
    const view = new DataView(padded.buffer, off, 64)
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(i * 4, false)
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(7, w[i - 15]) ^ rotr(18, w[i - 15]) ^ (w[i - 15] >>> 3)
      const s1 = rotr(17, w[i - 2]) ^ rotr(19, w[i - 2]) ^ (w[i - 2] >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0
    }
    let [a, b, c, d, e, f, g, h] = [h0, h1, h2, h3, h4, h5, h6, h7]
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e)
      const ch = (e & f) ^ (~e & g)
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0
      const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) | 0
      h = g
      g = f
      f = e
      e = (d + t1) | 0
      d = c
      c = b
      b = a
      a = (t1 + t2) | 0
    }
    h0 = (h0 + a) | 0
    h1 = (h1 + b) | 0
    h2 = (h2 + c) | 0
    h3 = (h3 + d) | 0
    h4 = (h4 + e) | 0
    h5 = (h5 + f) | 0
    h6 = (h6 + g) | 0
    h7 = (h7 + h) | 0
  }

  const result = new ArrayBuffer(32)
  const resultView = new DataView(result)
  ;[h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => resultView.setUint32(i * 4, v, false))
  return result
}

export const createCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const verifierBytes = new TextEncoder().encode(codeVerifier)
  const digest = window.crypto?.subtle
    ? await window.crypto.subtle.digest('SHA-256', verifierBytes)
    : await sha256Fallback(verifierBytes)

  return encodeBase64Url(new Uint8Array(digest))
}

export const createPkcePair = async (): Promise<{codeChallenge: string; codeVerifier: string}> => {
  const codeVerifier = createCodeVerifier()
  const codeChallenge = await createCodeChallenge(codeVerifier)

  return {codeChallenge, codeVerifier}
}
